import { useContext, useState, useMemo } from 'react'
import { Layout, theme, Dropdown, Form, Modal, Badge, Button } from 'antd'
import Avatar from '@mui/material/Avatar'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { VerifiedUser, LockOpen, Notifications } from '@mui/icons-material'

import TextInput from 'components/CustomFormItem'
import { LayoutContext } from 'contexts'
import { Search } from 'assets/images'
import { useGetUserImageFromS3 } from 'services/auth/auth.service'

import {
  StyledLink,
  StyledDropDownMenuWrapper,
  StyledMenu,
  StyledButtonWrapper,
  StyledDropDownLink,
  StyledDropdownBtn,
  StyledUserName,
  StyledIRESSVerifiedStatusWrapper,
  StyledIRESSVerifiedStatusLabel,
  StyledIRESSVerificationStatus,
  StyledIRESSVerificationStatusIcon,
  StyledLogo,
  StyledNotificationButton,
  StyledNotificationIcon,
  StyledModalContent,
  StyledLoadingState,
  StyledErrorState,
  StyledEmptyState,
  StyledNotesCounter,
  StyledAgentCard,
  StyledAgentName,
  StyledNoteItem,
  StyledNoteMetadata,
  StyledNoteContent,
  StyledHeaderActions,
} from './Header.styles'
import { HeaderTypes } from './Header.types'
import { eventManager } from 'utils'
import { LogoWithoutText } from 'assets/images'
import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { getAgentNotesQuery } from 'gql/client'
import { useQuery } from '@apollo/client'

const { Header } = Layout

const currentDate = new Date().toISOString().split('T')[0]

// Helper function to format timestamps
const formatTimestamp = (timestamp: string | null) => {
  if (!timestamp) return 'No date'

  // Convert string timestamp to number if it's stored as string
  const timestampNum = Number(timestamp)
  if (isNaN(timestampNum)) return timestamp

  return new Date(timestampNum).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Helper to check if a date is today
const isToday = (timestamp: string | null) => {
  if (!timestamp) return false

  const timestampNum = Number(timestamp)
  if (isNaN(timestampNum)) return false

  const date = new Date(timestampNum)
  const today = new Date()

  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
}

const CustomHeader = ({ user, handleLogout, headerCustomComponent }: HeaderTypes) => {
  const {
    token: { colorBgContainer },
  } = theme.useToken()
  const { sideBarWidth } = useContext(LayoutContext)
  const navigate = useNavigate()
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState(false)

  const { data, error, loading } = useQuery(getAgentNotesQuery, {
    variables: { date: currentDate },
  })

  // Process the data to find agents with notes for today
  const { agentsWithTodayNotes, todayNotesCount } = useMemo(() => {
    if (!data?.getAgentNotes) return { agentsWithTodayNotes: [], todayNotesCount: 0 }

    const agentsWithNotes = data.getAgentNotes
      .map((agent: any) => {
        // Filter notes that have a followUpDate for today
        const todayNotes = (agent.notes || []).filter((note: any) => isToday(note.followUpDate))

        return {
          ...agent,
          todayNotes,
        }
      })
      .filter((agent: any) => agent.todayNotes.length > 0)

    // Count total notes for today across all agents
    const totalNotes = agentsWithNotes.reduce((sum: number, agent: any) => sum + agent.todayNotes.length, 0)

    return {
      agentsWithTodayNotes: agentsWithNotes,
      todayNotesCount: totalNotes,
    }
  }, [data])

  const { url } = useGetUserImageFromS3({
    filePath: user?.avatar,
  })

  const { firstName, isIressCredentialsVerified } = user

  const openIRESSDetailsModal = () => {
    eventManager.openIressDetailsModal(
      {},
      {
        confirm: () => null,
      },
    )
  }

  const showNotificationModal = () => {
    setIsNotificationModalVisible(true)
  }

  const handleNotificationModalClose = () => {
    setIsNotificationModalVisible(false)
  }

  const NotificationButton = () => (
    <StyledNotificationButton onClick={showNotificationModal}>
      <Badge count={todayNotesCount} size="small">
        <StyledNotificationIcon hasNotifications={todayNotesCount > 0}>
          <Notifications style={{ fontSize: '24px' }} />
        </StyledNotificationIcon>
      </Badge>
    </StyledNotificationButton>
  )

  const menu = (
    <StyledMenu>
      <Avatar
        src={url}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '4px',
        }}
      />
      <br />
      <h1>{firstName}</h1>
      <StyledButtonWrapper>
        <StyledLink to={`/user-profile/${user._id}`}>
          <StyledDropdownBtn>Profile</StyledDropdownBtn>
        </StyledLink>
        <StyledDropdownBtn onClick={handleLogout}>Log out</StyledDropdownBtn>
      </StyledButtonWrapper>
    </StyledMenu>
  )

  const renderNotesModal = () => (
    <Modal
      title={`Follow-up Notes for Today (${formatTimestamp(new Date().getTime().toString())})`}
      open={isNotificationModalVisible}
      onCancel={handleNotificationModalClose}
      footer={[
        <Button key="close" onClick={handleNotificationModalClose}>
          Close
        </Button>,
      ]}
      width={600}
    >
      <StyledModalContent>
        {loading ? (
          <StyledLoadingState>Loading...</StyledLoadingState>
        ) : error ? (
          <StyledErrorState>Error loading notifications</StyledErrorState>
        ) : todayNotesCount === 0 ? (
          <StyledEmptyState>No follow-up notes for today</StyledEmptyState>
        ) : (
          <div>
            <StyledNotesCounter>
              {todayNotesCount} note{todayNotesCount !== 1 ? 's' : ''} requiring follow-up today
            </StyledNotesCounter>

            {agentsWithTodayNotes.map((agent: any) => (
              <StyledAgentCard key={agent._id}>
                <StyledAgentName>
                  {agent.firstName} {agent.lastName}
                </StyledAgentName>

                {agent.todayNotes.map((note: any) => (
                  <StyledNoteItem key={note._id}>
                    <StyledNoteMetadata>
                      <span>Created: {formatTimestamp(note.createdAt)}</span>
                    </StyledNoteMetadata>
                    <StyledNoteContent>{note.description}</StyledNoteContent>
                  </StyledNoteItem>
                ))}
              </StyledAgentCard>
            ))}
          </div>
        )}
      </StyledModalContent>
    </Modal>
  )

  if (isMobile) {
    return (
      <>
        <Header
          style={{
            height: '80px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px',
            background: colorBgContainer,
            borderBottom: '1px solid #A8D6EA',
            position: 'fixed',
            width: `100%`,
            zIndex: 2,
          }}
        >
          <StyledLogo width={48} src={LogoWithoutText} onClick={() => navigate('/resources')} />

          <StyledHeaderActions>
            <NotificationButton />
            <StyledDropDownMenuWrapper>
              <StyledIRESSVerifiedStatusWrapper verified={isIressCredentialsVerified} onClick={openIRESSDetailsModal}>
                <StyledIRESSVerificationStatus verified={isIressCredentialsVerified}>
                  <StyledIRESSVerifiedStatusLabel>IRESS: </StyledIRESSVerifiedStatusLabel>
                  <StyledIRESSVerificationStatusIcon>
                    {isIressCredentialsVerified ? <VerifiedUser style={{ fontSize: '16px' }} /> : <LockOpen style={{ fontSize: '16px' }} />}
                  </StyledIRESSVerificationStatusIcon>
                  {isIressCredentialsVerified ? 'Verified' : 'Not Verified, Verify Now'}
                </StyledIRESSVerificationStatus>
              </StyledIRESSVerifiedStatusWrapper>

              <Avatar
                src={url}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                }}
              />
              <Dropdown overlay={menu} overlayStyle={{ position: 'fixed' }} trigger={['click']}>
                <StyledDropDownLink className="ant-dropdown-link" href="/" onClick={(e) => e.preventDefault()}>
                  <StyledUserName className="hide-mobile">{firstName}</StyledUserName>
                  <ExpandMoreIcon style={{ fill: '#323232' }} />
                </StyledDropDownLink>
              </Dropdown>
            </StyledDropDownMenuWrapper>
          </StyledHeaderActions>
        </Header>

        {renderNotesModal()}
      </>
    )
  }

  return (
    <>
      <Header
        style={{
          height: '80px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 96px',
          background: colorBgContainer,
          borderBottom: '1px solid #A8D6EA',
          position: 'fixed',
          width: `calc(100% - ${sideBarWidth}px)`,
          zIndex: 2,
        }}
      >
        {!!headerCustomComponent ? (
          headerCustomComponent
        ) : (
          <Form>
            <TextInput width={'343px'} name={'searchBar'} placeholder="Coming Soon..." inputPrefixImg={Search} />
          </Form>
        )}
        <StyledHeaderActions>
          <StyledDropDownMenuWrapper>
            <NotificationButton />

            <StyledIRESSVerifiedStatusWrapper verified={isIressCredentialsVerified} onClick={openIRESSDetailsModal}>
              <StyledIRESSVerifiedStatusLabel>IRESS Status: </StyledIRESSVerifiedStatusLabel>
              <StyledIRESSVerificationStatus verified={isIressCredentialsVerified}>
                <StyledIRESSVerificationStatusIcon>
                  {isIressCredentialsVerified ? <VerifiedUser style={{ fontSize: '16px' }} /> : <LockOpen style={{ fontSize: '16px' }} />}
                </StyledIRESSVerificationStatusIcon>
                {isIressCredentialsVerified ? 'Verified' : 'Not Verified, Verify Now'}
              </StyledIRESSVerificationStatus>
            </StyledIRESSVerifiedStatusWrapper>

            <Avatar
              src={url}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '4px',
              }}
            />
            <Dropdown overlay={menu} overlayStyle={{ position: 'fixed' }} trigger={['click']}>
              <StyledDropDownLink className="ant-dropdown-link" href="/" onClick={(e) => e.preventDefault()}>
                <StyledUserName className="hide-mobile">{firstName}</StyledUserName>
                <ExpandMoreIcon style={{ fill: '#323232' }} />
              </StyledDropDownLink>
            </Dropdown>
          </StyledDropDownMenuWrapper>
        </StyledHeaderActions>
      </Header>

      {renderNotesModal()}
    </>
  )
}

export default CustomHeader
